import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit2, Trash2, GripVertical, Zap, RefreshCw,
  AlertCircle, Hash, MessageSquare, Loader2, Tag,
  TrendingUp, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch }   from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api       from '@/lib/api';
import { toast } from 'sonner';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0  },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay },
});

const EMPTY_KEYWORD  = { keyword: '', category: 'general', priority: 0, isActive: true };
const EMPTY_TEMPLATE = { keywordId: '', templateText: '', variables: {}, isActive: true };

export default function Automation() {
  const [keywords,   setKeywords]   = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [settings,   setSettings]   = useState({ is_bot_active: false, welcome_message: '', fallback_message: '' });
  const [loading,    setLoading]    = useState(true);

  const [kwDialog,   setKwDialog]   = useState(false);
  const [tmpDialog,  setTmpDialog]  = useState(false);
  const [editingKw,  setEditingKw]  = useState(null);
  const [editingTmp, setEditingTmp] = useState(null);
  const [savingKw,   setSavingKw]   = useState(false);
  const [savingTmp,  setSavingTmp]  = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [kwForm,  setKwForm]  = useState(EMPTY_KEYWORD);
  const [tmpForm, setTmpForm] = useState(EMPTY_TEMPLATE);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [kwRes, tmpRes, setRes] = await Promise.all([
        api.get('/business/keywords'),
        api.get('/business/templates'),
        api.get('/business/settings'),
      ]);
      setKeywords(kwRes.data.keywords   || []);
      setTemplates(tmpRes.data.templates || []);
      setSettings(setRes.data.settings   || {});
    } catch {
      toast.error('Failed to load automation data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAutomation = async (enabled) => {
    try {
      await api.put('/business/settings', {
        isBotActive:     enabled,
        businessHours:   settings.business_hours,
        location:        settings.location,
        welcomeMessage:  settings.welcome_message,
        fallbackMessage: settings.fallback_message,
      });
      setSettings({ ...settings, is_bot_active: enabled });
      toast.success(`Automation ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update automation');
    }
  };

  // ─── keyword actions ───────────────────────────────────────────────────────
  const handleSaveKw = async () => {
    if (!kwForm.keyword || kwForm.keyword.trim().length < 2) {
      toast.error('Keyword must be at least 2 characters'); return;
    }
    if (editingKw) {
      toast.info('Keyword update not supported. Please delete and create new.'); return;
    }
    setSavingKw(true);
    try {
      await api.post('/business/keywords', kwForm);
      toast.success('Keyword created');
      setKwDialog(false); setKwForm(EMPTY_KEYWORD); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save keyword');
    } finally {
      setSavingKw(false);
    }
  };

  const handleDeleteKw = async (id) => {
    if (!window.confirm('Delete this keyword? Associated templates will also be affected.')) return;
    setDeletingId(id);
    try {
      await api.delete(`/business/keywords/${id}`);
      toast.success('Keyword deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete keyword');
    } finally {
      setDeletingId(null);
    }
  };

  // ─── template actions ──────────────────────────────────────────────────────
  const handleSaveTmp = async () => {
    if (!tmpForm.keywordId) { toast.error('Please select a keyword'); return; }
    if (!tmpForm.templateText || tmpForm.templateText.trim().length < 5) {
      toast.error('Template text must be at least 5 characters'); return;
    }
    setSavingTmp(true);
    try {
      if (editingTmp) {
        await api.put(`/business/templates/${editingTmp.id}`, tmpForm);
        toast.success('Template updated');
      } else {
        await api.post('/business/templates', tmpForm);
        toast.success('Template created');
      }
      setTmpDialog(false); setEditingTmp(null); setTmpForm(EMPTY_TEMPLATE); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSavingTmp(false);
    }
  };

  const handleDeleteTmp = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/business/templates/${id}`);
      toast.success('Template deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete template');
    } finally {
      setDeletingId(null);
    }
  };

  const openEditTmp = (t) => {
    setEditingTmp(t);
    setTmpForm({ keywordId: t.keyword_id, templateText: t.template_text, variables: t.variables || {}, isActive: t.is_active });
    setTmpDialog(true);
  };

  const openAddKw  = () => { setEditingKw(null);  setKwForm(EMPTY_KEYWORD);   setKwDialog(true);  };
  const openAddTmp = () => { setEditingTmp(null); setTmpForm(EMPTY_TEMPLATE); setTmpDialog(true); };

  // ─── loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="h-[calc(100vh-140px)] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
          <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
        </div>
        <p className="text-sm text-gray-400 font-medium">Loading automation…</p>
      </div>
    </div>
  );

  // ─── stats ─────────────────────────────────────────────────────────────────
  const activeKw   = keywords.filter(k => k.is_active).length;
  const totalUsage = keywords.reduce((s, k) => s + (k.usage_count || 0), 0);

  // ─── render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8" data-testid="automation-page">

      {/* Header */}
      <motion.div {...fadeUp(0)} className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Automation</h1>
          <p className="mt-1 text-sm text-gray-400">Configure keyword-based auto-replies for your WhatsApp bot.</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          aria-label="Refresh automation data"
          className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </motion.div>

      {/* Stat chips */}
      <motion.div {...fadeUp(0.06)} className="flex flex-wrap gap-3">
        {[
          { icon: Hash,         label: 'Keywords',    value: keywords.length  },
          { icon: ToggleRight,  label: 'Active KW',   value: activeKw         },
          { icon: MessageSquare,label: 'Templates',   value: templates.length },
          { icon: TrendingUp,   label: 'Total Usage', value: totalUsage       },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-2.5 px-4 py-2 bg-white border border-gray-100 rounded-xl shadow-sm">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div>
              <span className="text-xs text-gray-400 block leading-none mb-0.5">{label}</span>
              <span className="text-sm font-bold text-gray-900">{value}</span>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Bot Status Card */}
      <motion.div {...fadeUp(0.1)}>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${settings.is_bot_active ? 'bg-emerald-500' : 'bg-gray-100'}`}>
                <Zap className={`w-5 h-5 ${settings.is_bot_active ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Bot Status</h2>
                <p className={`text-xs font-medium ${settings.is_bot_active ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {settings.is_bot_active ? 'Active — Responding to customers' : 'Inactive — Not responding'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.is_bot_active}
              onCheckedChange={toggleAutomation}
              data-testid="automation-master-toggle"
              className="data-[state=checked]:bg-emerald-500"
            />
          </div>

          <p className="text-sm text-gray-500 mb-4">
            {settings.is_bot_active
              ? 'Your bot is actively sending keyword-based replies to customers 24/7.'
              : 'Automation is currently disabled. Enable it to start auto-responding to customers.'}
          </p>

          <div className="space-y-3 pt-4 border-t border-gray-100">
            {settings.welcome_message && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Welcome Message</p>
                <p className="text-sm text-gray-600 leading-relaxed">{settings.welcome_message}</p>
              </div>
            )}
            {settings.fallback_message && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Fallback Message</p>
                <p className="text-sm text-gray-600 leading-relaxed">{settings.fallback_message}</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Info alert */}
      <motion.div {...fadeUp(0.14)}>
        <div className="flex items-center gap-3 p-3.5 rounded-xl bg-blue-50 border border-blue-100">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-xs text-blue-700 leading-relaxed">
            Add keywords that customers might send (e.g. "price", "hours", "location") and set a reply template for each. The bot will automatically send the matching reply.
          </p>
        </div>
      </motion.div>

      {/* Keywords Section */}
      <motion.div {...fadeUp(0.18)}>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Hash className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Keywords</h2>
            </div>
            <button
              onClick={openAddKw}
              data-testid="add-keyword-button"
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-md shadow-emerald-200 transition-all"
            >
              <Plus className="w-4 h-4" />
              Add Keyword
            </button>
          </div>

          <div className="p-4 space-y-2" data-testid="keywords-list">
            <AnimatePresence>
              {keywords.length > 0 ? keywords.map((kw, i) => (
                <motion.div
                  key={kw.id}
                  {...fadeUp(i * 0.03)}
                  exit={{ opacity: 0, y: -8 }}
                  data-testid={`keyword-item-${kw.id}`}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-md hover:shadow-emerald-50 bg-white transition-all"
                >
                  <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">{kw.keyword}</span>
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium">
                        {kw.category || 'general'}
                      </span>
                      {!kw.is_active && (
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 text-[10px] font-medium">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>Priority: <strong className="text-gray-600">{kw.priority || 0}</strong></span>
                      <span>Usage: <strong className="text-gray-600">{kw.usage_count || 0}</strong></span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteKw(kw.id)}
                    disabled={deletingId === kw.id}
                    data-testid={`delete-keyword-${kw.id}`}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-50 flex-shrink-0"
                  >
                    {deletingId === kw.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />
                    }
                  </button>
                </motion.div>
              )) : (
                <div className="text-center py-12">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                    <Hash className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">No keywords yet</p>
                  <p className="text-xs text-gray-400 mt-1">Add your first keyword to start automating replies.</p>
                  <button
                    onClick={openAddKw}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Keyword
                  </button>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Templates Section */}
      <motion.div {...fadeUp(0.22)}>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900">Response Templates</h2>
            </div>
            <button
              onClick={openAddTmp}
              disabled={keywords.length === 0}
              data-testid="add-template-button"
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-md shadow-emerald-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Add Template
            </button>
          </div>

          <div className="p-4 space-y-2" data-testid="templates-list">
            <AnimatePresence>
              {templates.length > 0 ? templates.map((tmp, i) => {
                const relKw = keywords.find(k => k.id === tmp.keyword_id);
                return (
                  <motion.div
                    key={tmp.id}
                    {...fadeUp(i * 0.03)}
                    exit={{ opacity: 0, y: -8 }}
                    data-testid={`template-item-${tmp.id}`}
                    className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-md hover:shadow-emerald-50 bg-white transition-all"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-100 text-xs font-semibold text-emerald-700">
                          <Tag className="w-3 h-3" />
                          {relKw?.keyword || 'Unknown'}
                        </span>
                        {!tmp.is_active && (
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 text-[10px] font-medium">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {tmp.template_text}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => openEditTmp(tmp)}
                        data-testid={`edit-template-${tmp.id}`}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTmp(tmp.id)}
                        disabled={deletingId === tmp.id}
                        data-testid={`delete-template-${tmp.id}`}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-50"
                      >
                        {deletingId === tmp.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4" />
                        }
                      </button>
                    </div>
                  </motion.div>
                );
              }) : (
                <div className="text-center py-12">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                    <MessageSquare className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">No templates yet</p>
                  {keywords.length === 0 ? (
                    <p className="text-xs text-gray-400 mt-1">Create keywords first, then add reply templates.</p>
                  ) : (
                    <>
                      <p className="text-xs text-gray-400 mt-1">Add your first template to automate replies.</p>
                      <button
                        onClick={openAddTmp}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add First Template
                      </button>
                    </>
                  )}
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Add Keyword Dialog */}
      <Dialog open={kwDialog} onOpenChange={setKwDialog}>
        <DialogContent className="sm:max-w-lg rounded-2xl border-gray-100 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Add New Keyword</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Keyword <span className="text-rose-400">*</span>
              </Label>
              <Input
                value={kwForm.keyword}
                onChange={e => setKwForm({ ...kwForm, keyword: e.target.value })}
                placeholder="e.g., pricing, hours, location"
                data-testid="keyword-input"
                className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Category</Label>
              <Input
                value={kwForm.category}
                onChange={e => setKwForm({ ...kwForm, category: e.target.value })}
                placeholder="general, support, sales"
                data-testid="category-input"
                className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Priority (0–10)</Label>
              <Input
                type="number" min="0" max="10"
                value={kwForm.priority}
                onChange={e => setKwForm({ ...kwForm, priority: parseInt(e.target.value) || 0 })}
                data-testid="priority-input"
                className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm"
              />
            </div>
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50 border border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Active</p>
                <p className="text-xs text-gray-400">Keyword will trigger auto-replies when active.</p>
              </div>
              <Switch
                checked={kwForm.isActive}
                onCheckedChange={v => setKwForm({ ...kwForm, isActive: v })}
                data-testid="keyword-active-toggle"
                className="data-[state=checked]:bg-emerald-500"
              />
            </div>
            <button
              onClick={handleSaveKw}
              disabled={savingKw}
              data-testid="save-keyword-button"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 transition-all"
            >
              {savingKw
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                : 'Add Keyword'
              }
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Template Dialog */}
      <Dialog open={tmpDialog} onOpenChange={setTmpDialog}>
        <DialogContent className="sm:max-w-2xl rounded-2xl border-gray-100 shadow-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {editingTmp ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Keyword <span className="text-rose-400">*</span>
              </Label>
              <Select value={tmpForm.keywordId} onValueChange={v => setTmpForm({ ...tmpForm, keywordId: v })}>
                <SelectTrigger className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 text-sm" data-testid="template-keyword-select">
                  <SelectValue placeholder="Select a keyword" />
                </SelectTrigger>
                <SelectContent>
                  {keywords.filter(k => k.is_active).map(k => (
                    <SelectItem key={k.id} value={k.id}>{k.keyword} ({k.category})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Response Template <span className="text-rose-400">*</span>
              </Label>
              <Textarea
                value={tmpForm.templateText}
                onChange={e => setTmpForm({ ...tmpForm, templateText: e.target.value })}
                placeholder="Bot will send this message when the keyword is detected…"
                rows={6}
                data-testid="template-text-input"
                className="rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 text-sm resize-none"
              />
              <p className="text-xs text-gray-400">
                You can use variables like{' '}
                <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-600">{'{customer_name}'}</code>,{' '}
                <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-600">{'{business_name}'}</code>, etc.
              </p>
            </div>
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50 border border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Active</p>
                <p className="text-xs text-gray-400">Template will be used when active.</p>
              </div>
              <Switch
                checked={tmpForm.isActive}
                onCheckedChange={v => setTmpForm({ ...tmpForm, isActive: v })}
                data-testid="template-active-toggle"
                className="data-[state=checked]:bg-emerald-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setTmpDialog(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTmp}
                disabled={savingTmp}
                data-testid="save-template-button"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 transition-all"
              >
                {savingTmp
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                  : editingTmp ? 'Update Template' : 'Create Template'
                }
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
